import Components from "@pages/components";
import { useEffect } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";

import { Toaster } from "@/components/ui/sonner";
import {
  AddBlogs,
  Auth,
  Blogs,
  BrandingType,
  Careers,
  Certification,
  Courses,
  CustomPage,
  Dashboard,
  Faqs,
  Forms,
  IndustryAllies,
  Invoice,
  Journey,
  Mentors,
  Moments,
  Payment,
  PaymentMode,
  Policy,
  Profile,
  Response,
  RoleAccess,
  StudentPlacement,
  Students,
  Success,
  Team,
  Test,
  Testimonials,
  EnterpriseTestimonials,
  EnterpriseCherishMoments,
  HomeCherishMoments,
  CoursesSetup,
  FormsSetup,
  Advertisment,
  Bucket,
  ActivityPage,
  BlogsC,
  BlogCategoriesC,
  AddBlogsC,
  Redirects,
  CertificateWebhook,
} from "@/pages";
import CricketMatch from "@/pages/dashboard/marketingEvents/cricket/match";
import CricketWinners from "@/pages/dashboard/marketingEvents/cricket/match/winners";
import ManageCricketTeams from "@/pages/dashboard/marketingEvents/cricket/teams";
import CricketPredictions from "@/pages/dashboard/marketingEvents/cricket/prediction";
import CricketUserDetails from "@/pages/dashboard/marketingEvents/cricket/userDetails";

import SiteSettings from "@/pages/dashboard/site-settings";
import GiveawayTemplate from "@/pages/dashboard/giveaway-template";
import ManageSource from "@pages/dashboard/manage-source";
import MicroSoftTeams from "@pages/dashboard/microsoft-teams";
import { ErrorIcon, ExclamationDangerIcon, SuccessIcon } from "@assets/icons";
import { Alert, AlertDescription, AlertTitle } from "@components/ui/alert";
import NotFound, { PageLoader } from "@pages/not-found/index";
import {
  defaultShouldDehydrateQuery,
  isServer,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { initSentry } from "@utils/function/sentry.function";
import { useNetwork } from "@utils/hook/index.hook";
import { useSelectionStore, useUser } from "@utils/states/index.state";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ErrorBound from "@ui/internal/components/errorbound/ErrorBound";
import PrivateLayout from "@utils/layout/index.layout";
import Cookies from "js-cookie";
import BlogCategories from "@pages/dashboard/blogs/categories";
import FloatingClearCache from "@ui/internal/components/clear-cache/floatingClearCache";

initSentry();

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
      },
      dehydrate: {
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === "pending",
      },
    },
  });
}
let browserQueryClient: QueryClient | undefined = undefined;
function getQueryClient() {
  if (isServer) {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

const App = () => {
  const queryClient = getQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  // console.log("THIIS IS THE MICROSOFT TEAMS COMPONENT", <MicroSoftTeams />);

  // *************************************** reset table selection on location change *************************************** //
  const { setSelectedRows, setSelectedRowDetails } = useSelectionStore();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    setSelectedRows([]);
    setSelectedRowDetails([]);
    searchParams.delete("data-category");
    setSearchParams(() => searchParams);
  }, [location.pathname]);

  const { user, isPending } = useUser();

  // *************************************** Check if user is authenticated *************************************** //
  const isAuthenticated =
    Cookies.get("access") || sessionStorage.getItem("access");

  // *************************************** Redirect to login if accessing protected routes without auth *************************************** //
  useEffect(() => {
    const authRoutes = [
      "/login",
      "/forget-password",
      "/reset-verify-otp",
      "/reset-password",
    ];
    const isAuthRoute = authRoutes.includes(location.pathname);

    if (!isAuthenticated && !isAuthRoute && location.pathname !== "/") {
      navigate("/login");
    }
  }, [location.pathname, isAuthenticated, navigate]);

  const { models_with_permission } = user;
  const hasPermission = (model: string) => {
    return models_with_permission?.some(
      (permission: { name: string }) => permission?.name === model,
    );
  };

  // *************************************** all routes under dashboard *************************************** //
  const routes = [
    // General Pages
    { path: "/", element: <></>, active: true },
    { path: "/blogs", element: <Blogs />, active: hasPermission("blog") },
    {
      path: "/blogs-category",
      element: <BlogCategories />,
      active: hasPermission("blog"),
    },
    { path: "/blogsC", element: <BlogsC />, active: hasPermission("blog") },
    {
      path: "/blogsC-category",
      element: <BlogCategoriesC />,
      active: hasPermission("blog"),
    },
    { path: "/careers", element: <Careers />, active: hasPermission("career") },
    { path: "/courses", element: <Courses />, active: hasPermission("course") },
    {
      path: "/masterclass-courses",
      element: <Courses />,
      active: hasPermission("course"),
    },
    {
      path: "/see-courses",
      element: <Courses />,
      active: hasPermission("course"),
    },
    {
      path: "/plus2-courses",
      element: <Courses />,
      active: hasPermission("course"),
    },
    {
      path: "/jrp-courses",
      element: <Courses />,
      active: hasPermission("course"),
    },
    {
      path: "/courses/:setup",
      element: <CoursesSetup />,
      active: hasPermission("course"),
    },
    {
      path: "/masterclass-courses/:setup",
      element: <CoursesSetup />,
      active: hasPermission("course"),
    },
    {
      path: "/jrp-courses/:setup",
      element: <CoursesSetup />,
      active: hasPermission("course"),
    },
    { path: "/faqs", element: <Faqs />, active: hasPermission("faqs") },
    { path: "/forms", element: <Forms />, active: hasPermission("forms") },
    {
      path: "/forms/:setup",
      element: <FormsSetup />,
      active: hasPermission("forms"),
    },
    {
      path: "/journey",
      element: <Journey />,
      active: hasPermission("journey"),
    },
    {
      path: "/moments",
      element: <Moments />,
      active: hasPermission("moments"),
    },
    {
      path: "/success-stories",
      element: <Success />,
      active: hasPermission("successstory"),
    },
    {
      path: "/testimonials",
      element: <Testimonials />,
      active: hasPermission("testimonial"),
    },
    {
      path: "/enterprise-testimonials",
      element: <EnterpriseTestimonials />,
      active: true,
    },
    {
      path: "/enterprise-cherish-moments",
      element: <EnterpriseCherishMoments />,
      active: true,
    },
    {
      path: "/home-cherish-moments",
      element: <HomeCherishMoments />,
      active: true,
    },

    // Legal
    {
      path: "/privacy-policy",
      element: <Policy />,
      active: hasPermission("privacypolicy"),
    },
    {
      path: "/terms-&-condition",
      element: <Policy />,
      active: hasPermission("termsconditions"),
    },
    {
      path: "/bucket",
      element: <Bucket />,
      active: true,
    },
    {
      path: "/activity-log",
      element: <ActivityPage />,
      active: true,
    },
    // Dynamic Routes
    {
      path: "/response/:type",
      element: <Response />,
      active:
        hasPermission("enrollment") ||
        hasPermission("requestform") ||
        hasPermission("responseform") ||
        hasPermission("contactus"),
    },
    {
      path: "/certificate/:type",
      element: <Certification />,
      active: hasPermission("certificate"),
    },
    {
      path: "/certificate-webhook",
      element: <CertificateWebhook />,
      active: hasPermission("certificate"),
    },
    {
      path: "/certificate/:type/:id",
      element: <BrandingType />,
      active: hasPermission("certificate"),
    },

    // Team & Access
    {
      path: "/team-members",
      element: <Team />,
      active: hasPermission("teammember"),
    },
    {
      path: "/mentors",
      element: <Mentors />,
      active: hasPermission("mentors"),
    },
    {
      path: "/industry-allies",
      element: <IndustryAllies />,
      active: hasPermission("industryallies"),
    },
    {
      path: "/roles-&-access",
      element: <RoleAccess />,
      active: hasPermission("group"),
    },

    // Students
    {
      path: "/students",
      element: <Students />,
      active: hasPermission("student"),
    },
    {
      path: "/masterclass-students",
      element: <Students />,
      active: hasPermission("student"),
    },
    {
      path: "/student-placement",
      element: <StudentPlacement />,
      active: hasPermission("studentplacement"),
    },

    // Profile
    { path: "/profile", element: <Profile />, active: true },

    // Payment
    {
      path: "/invoice",
      element: <Invoice />,
      active: hasPermission("invoice"),
    },
    {
      path: "/masterclass-invoice",
      element: <Invoice />,
      active: hasPermission("invoice"),
    },
    {
      path: "/jrp-invoice",
      element: <Invoice />,
      active: hasPermission("invoice"),
    },
    {
      path: "/payment",
      element: <Payment />,
      active: hasPermission("payment"),
    },
    {
      path: "/masterclass-payment",
      element: <Payment />,
      active: hasPermission("payment"),
    },
    {
      path: "/jrp-payment",
      element: <Payment />,
      active: hasPermission("payment"),
    },
    {
      path: "/payment/:setup",
      element: <PaymentMode />,
      active: hasPermission("payment"),
    },
    {
      path: "/masterclass-payment/:setup",
      element: <PaymentMode />,
      active: hasPermission("payment"),
    },
    {
      path: "/jrp-payment/:setup",
      element: <PaymentMode />,
      active: hasPermission("payment"),
    },
    {
      path: "/advertisment/:type",
      element: <Advertisment />,
      active: true,
    },

    // Catch-all
    { path: "*", element: <NotFound /> },

    // Custom page
    {
      path: "/custom-page",
      element: <CustomPage />,
      active: hasPermission("custompage"),
    },

    {
      path: "/giveaway-templates",
      element: <GiveawayTemplate />,
      active: true,
    },

    {
      path: "response/giveaway/:type",
      element: <ManageSource />,
      active: true,
    },

    {
      path: "/site-settings",
      element: <SiteSettings />,
      active: true,
    },

    //------Cricket Paths starts here

    {
      path: "/cricket-match",
      element: <CricketMatch />,
      active: true,
    },

    {
      path: "/cricket-match/winners",
      element: <CricketWinners />,
      active: true,
    },

    {
      path: "/cricket-teams",
      element: <ManageCricketTeams />,
      active: true,
    },

    {
      path: "/cricket-predictions",
      element: <CricketPredictions />,
      active: true,
    },

    {
      path: "/cricket-userdetails",
      element: <CricketUserDetails />,
      active: true,
    },

    // ------Cricket Paths end here

    {
      path: "/redirects",
      element: <Redirects />,
      active: true,
    },

    {
      path: "microsoft-teams",
      element: <MicroSoftTeams />,
      // active: hasPermission("microsoft-teams")
      active: true,
    },

    // Dev page
    ...(import.meta.env.MODE === "development"
      ? [
          { path: "/components", element: <Components /> },
          { path: "/test", element: <Test /> },
        ]
      : []),
  ];

  // *************************************** all routes outside dashboard *************************************** //
  const authRoute = [
    { path: "/login", element: <Auth /> },
    { path: "/forget-password", element: <Auth /> },
    { path: "/reset-verify-otp", element: <Auth /> },
    { path: "/reset-password", element: <Auth /> },
  ];
  const blankRoute = [
    {
      path: "/add-blogs",
      element: <AddBlogs />,
      active: hasPermission("blog"),
    },
    {
      path: "/add-blogsC",
      element: <AddBlogsC />,
      active: hasPermission("blog"),
    },
  ];
  const { isOnline } = useNetwork();

  // *************************************** Handle root path redirect when authenticated *************************************** //
  if (["/"].includes(location.pathname) && isAuthenticated) {
    return <>{navigate("/journey?tpage=1")}</>;
  }

  return (
    <ErrorBound>
      {isAuthenticated && <FloatingClearCache />}
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        {authRoute.some((route) => route.path === location.pathname) ? (
          <Routes>
            {authRoute.map((item: any, index: number) => (
              <Route key={index} path={item.path} element={item.element} />
            ))}
          </Routes>
        ) : blankRoute.some((route) => route.path === location.pathname) ? (
          <Routes>
            <Route element={<PrivateLayout />}>
              {blankRoute
                // .filter((item) => item.active == true)
                .map((item: any, index: number) => (
                  <Route key={index} path={item.path} element={item.element} />
                ))}
            </Route>
            {/* <Route path="*" element={<NotFound />}></Route> */}
          </Routes>
        ) : (
          <Dashboard>
            <Routes>
              <Route element={<PrivateLayout />}>
                {routes
                  .filter((item) => item.active == true)
                  .map(({ path, element }, index) => (
                    <Route key={index} path={path} element={element} />
                  ))}
              </Route>
              <Route
                path="*"
                element={isPending ? <PageLoader /> : <NotFound />}
              ></Route>
            </Routes>
          </Dashboard>
        )}
        {!isOnline && (
          <Alert
            variant={"default"}
            className="fixed top-14 w-fit fade-in bg-slate-50 shadow-lg left-14 z-50"
          >
            {/* <WifiOff className="h-4 w-4" /> */}
            <AlertTitle className="flex items-center gap-2">
              <img src="./wifi.gif" alt="wifi" className="h-6 w-6" />
              Heads up!
            </AlertTitle>
            <AlertDescription className="text-black">
              Your Wifi Connection is not stable. Please check your internet
              connection.
            </AlertDescription>
          </Alert>
        )}
        <Toaster
          icons={{
            success: <SuccessIcon />,
            error: <ErrorIcon />,
            warning: <ExclamationDangerIcon />,
          }}
          closeButton
        />
      </QueryClientProvider>
    </ErrorBound>
  );
};

export default App;
